import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import {FormattedMessage, defineMessages, injectIntl, intlShape} from 'react-intl';
import Modal from '../../containers/modal.jsx';
import Box from '../box/box.jsx';
import {
    ADMIN_ROLE,
    deleteAdminProject,
    deleteAdminUser,
    fetchAdminOverview,
    listAdminProjects,
    listAdminUsers,
    resetAdminUserPassword,
    updateAdminUser
} from '../../lib/auth-api.js';
import {describeAuthError} from '../../lib/auth-error-messages.js';
import styles from './admin-panel.css';

/*
 * "管理后台": the administrator screen.
 *
 * Three tabs share one load: the overview counters, the account table and the
 * project table are all read together when the dialog opens, so switching tabs
 * never waits on the network.
 *
 * Destructive actions (delete an account, delete a project) ask for
 * confirmation inside the dialog rather than through window.confirm, which
 * blocks the page and cannot be styled or translated.
 *
 * The screen is only ever reachable from the account menu of an administrator;
 * the service rejects every request from anyone else regardless of what the
 * browser shows.
 */
const messages = defineMessages({
    title: {
        id: 'gui.admin.title',
        defaultMessage: '管理后台',
        description: 'Title of the administrator screen'
    },
    tabOverview: {
        id: 'gui.admin.tabOverview',
        defaultMessage: '概览',
        description: 'Overview tab label'
    },
    tabUsers: {
        id: 'gui.admin.tabUsers',
        defaultMessage: '用户',
        description: 'Users tab label'
    },
    tabProjects: {
        id: 'gui.admin.tabProjects',
        defaultMessage: '作品',
        description: 'Projects tab label'
    },
    statUsers: {
        id: 'gui.admin.statUsers',
        defaultMessage: '账号总数',
        description: 'Number of registered accounts'
    },
    statAdmins: {
        id: 'gui.admin.statAdmins',
        defaultMessage: '管理员',
        description: 'Number of administrator accounts'
    },
    statDisabled: {
        id: 'gui.admin.statDisabled',
        defaultMessage: '已禁用',
        description: 'Number of disabled accounts'
    },
    statProjects: {
        id: 'gui.admin.statProjects',
        defaultMessage: '作品总数',
        description: 'Number of stored projects'
    },
    statStorage: {
        id: 'gui.admin.statStorage',
        defaultMessage: '存储占用',
        description: 'Total stored project size'
    },
    columnAccount: {
        id: 'gui.admin.columnAccount',
        defaultMessage: '账号',
        description: 'Account name column heading'
    },
    columnDisplayName: {
        id: 'gui.admin.columnDisplayName',
        defaultMessage: '昵称',
        description: 'Display name column heading'
    },
    columnCreated: {
        id: 'gui.admin.columnCreated',
        defaultMessage: '注册时间',
        description: 'Registration time column heading'
    },
    columnProjects: {
        id: 'gui.admin.columnProjects',
        defaultMessage: '作品数',
        description: 'Project count column heading'
    },
    columnRole: {
        id: 'gui.admin.columnRole',
        defaultMessage: '角色',
        description: 'Role column heading'
    },
    columnStatus: {
        id: 'gui.admin.columnStatus',
        defaultMessage: '状态',
        description: 'Status column heading'
    },
    columnActions: {
        id: 'gui.admin.columnActions',
        defaultMessage: '操作',
        description: 'Actions column heading'
    },
    columnOwner: {
        id: 'gui.admin.columnOwner',
        defaultMessage: '所属账号',
        description: 'Project owner column heading'
    },
    columnSize: {
        id: 'gui.admin.columnSize',
        defaultMessage: '大小',
        description: 'Project size column heading'
    },
    columnUpdated: {
        id: 'gui.admin.columnUpdated',
        defaultMessage: '更新时间',
        description: 'Project update time column heading'
    },
    roleAdmin: {
        id: 'gui.admin.roleAdmin',
        defaultMessage: '管理员',
        description: 'Administrator role label'
    },
    roleUser: {
        id: 'gui.admin.roleUser',
        defaultMessage: '普通用户',
        description: 'Ordinary user role label'
    },
    statusActive: {
        id: 'gui.admin.statusActive',
        defaultMessage: '正常',
        description: 'Account is active'
    },
    statusDisabled: {
        id: 'gui.admin.statusDisabled',
        defaultMessage: '已禁用',
        description: 'Account is disabled'
    },
    disable: {
        id: 'gui.admin.disable',
        defaultMessage: '禁用',
        description: 'Disable an account'
    },
    enable: {
        id: 'gui.admin.enable',
        defaultMessage: '启用',
        description: 'Re-enable an account'
    },
    promote: {
        id: 'gui.admin.promote',
        defaultMessage: '设为管理员',
        description: 'Grant administrator rights'
    },
    demote: {
        id: 'gui.admin.demote',
        defaultMessage: '取消管理员',
        description: 'Revoke administrator rights'
    },
    resetPassword: {
        id: 'gui.admin.resetPassword',
        defaultMessage: '重置密码',
        description: 'Reset an account password'
    },
    remove: {
        id: 'gui.admin.remove',
        defaultMessage: '删除',
        description: 'Delete an account or project'
    },
    cancel: {
        id: 'gui.admin.cancel',
        defaultMessage: '取消',
        description: 'Cancel the pending action'
    },
    confirm: {
        id: 'gui.admin.confirm',
        defaultMessage: '确认',
        description: 'Confirm the pending action'
    },
    newPassword: {
        id: 'gui.admin.newPassword',
        defaultMessage: '新密码',
        description: 'Label of the reset-password field'
    },
    save: {
        id: 'gui.admin.save',
        defaultMessage: '保存',
        description: 'Save the new password'
    },
    loading: {
        id: 'gui.admin.loading',
        defaultMessage: '正在加载…',
        description: 'Shown while the administrator data loads'
    },
    emptyUsers: {
        id: 'gui.admin.emptyUsers',
        defaultMessage: '还没有任何账号。',
        description: 'Shown when no accounts exist'
    },
    emptyProjects: {
        id: 'gui.admin.emptyProjects',
        defaultMessage: '还没有任何作品。',
        description: 'Shown when no projects exist'
    },
    orphaned: {
        id: 'gui.admin.orphaned',
        defaultMessage: '（账号已删除）',
        description: 'Shown for a project whose owner no longer exists'
    },
    confirmDeleteUser: {
        id: 'gui.admin.confirmDeleteUser',
        defaultMessage: '确定删除账号 {account} 吗？该账号的作品也会一并删除，且无法恢复。',
        description: 'Confirmation before deleting an account'
    },
    confirmDeleteProject: {
        id: 'gui.admin.confirmDeleteProject',
        defaultMessage: '确定删除作品《{name}》吗？该操作无法恢复。',
        description: 'Confirmation before deleting a project'
    },
    confirmResetPassword: {
        id: 'gui.admin.confirmResetPassword',
        defaultMessage: '为账号 {account} 设置一个新密码（至少 10 位）：',
        description: 'Prompt shown before resetting a password'
    },
    passwordTooShort: {
        id: 'gui.admin.passwordTooShort',
        defaultMessage: '密码长度至少 10 位。',
        description: 'Shown when the new password is too short'
    },
    reload: {
        id: 'gui.admin.reload',
        defaultMessage: '刷新',
        description: 'Reload the administrator data'
    },
    done: {
        id: 'gui.admin.done',
        defaultMessage: '已完成。',
        description: 'Shown briefly after an action succeeds'
    }
});

const TAB_OVERVIEW = 'overview';
const TAB_USERS = 'users';
const TAB_PROJECTS = 'projects';
const MIN_PASSWORD_LENGTH = 10;

const formatBytes = bytes => {
    const value = Number(bytes) || 0;
    if (value < 1024) return `${value} B`;
    if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
    return `${(value / (1024 * 1024)).toFixed(1)} MB`;
};

class AdminPanel extends React.Component {
    constructor (props) {
        super(props);
        this.state = {
            tab: TAB_OVERVIEW,
            overview: null,
            users: [],
            projects: [],
            isLoading: true,
            errorCode: null,
            // The pending destructive action, if any. Holding it in state lets
            // the dialog ask for confirmation without a blocking window.confirm.
            pending: null,
            pendingValue: '',
            busyId: null,
            statusMessage: null
        };
        bindAll(this, [
            'handleAskDeleteProject',
            'handleAskDeleteUser',
            'handleAskResetPassword',
            'handleCancelPending',
            'handleConfirmPending',
            'handlePendingValueChange',
            'handleReload',
            'handleSelectOverview',
            'handleSelectProjects',
            'handleSelectUsers',
            'handleToggleDisabled',
            'handleToggleRole'
        ]);
    }

    componentDidMount () {
        this.handleReload();
    }

    handleReload () {
        this.setState({isLoading: true, errorCode: null, statusMessage: null});
        return Promise.all([
            fetchAdminOverview(),
            listAdminUsers(),
            listAdminProjects()
        ]).then(([overview, users, projects]) => {
            this.setState({
                overview: overview,
                users: users,
                projects: projects,
                isLoading: false
            });
        })
            .catch(error => {
                this.setState({
                    errorCode: (error && error.code) || 'NETWORK_ERROR',
                    isLoading: false
                });
            });
    }

    handleSelectOverview () {
        this.setState({tab: TAB_OVERVIEW});
    }

    handleSelectUsers () {
        this.setState({tab: TAB_USERS});
    }

    handleSelectProjects () {
        this.setState({tab: TAB_PROJECTS});
    }

    handlePendingValueChange (event) {
        this.setState({pendingValue: event.target.value});
    }

    handleCancelPending () {
        this.setState({pending: null, pendingValue: ''});
    }

    handleAskDeleteUser (user) {
        this.setState({pending: {type: 'deleteUser', user: user}, pendingValue: ''});
    }

    handleAskDeleteProject (project) {
        this.setState({pending: {type: 'deleteProject', project: project}, pendingValue: ''});
    }

    handleAskResetPassword (user) {
        this.setState({pending: {type: 'resetPassword', user: user}, pendingValue: ''});
    }

    handleToggleDisabled (user) {
        const nextDisabled = !user.disabled;
        this.setState({busyId: user.id, errorCode: null, statusMessage: null});
        updateAdminUser(user.id, {disabled: nextDisabled}).then(() => this.handleReload())
            .then(() => this.setState({
                busyId: null,
                statusMessage: nextDisabled ? 'disabled' : 'enabled'
            }))
            .catch(error => this.setState({
                busyId: null,
                errorCode: (error && error.code) || 'NETWORK_ERROR'
            }));
    }

    handleToggleRole (user) {
        const nextRole = user.role === ADMIN_ROLE ? 'user' : ADMIN_ROLE;
        this.setState({busyId: user.id, errorCode: null, statusMessage: null});
        updateAdminUser(user.id, {role: nextRole}).then(() => this.handleReload())
            .then(() => this.setState({busyId: null, statusMessage: 'roleChanged'}))
            .catch(error => this.setState({
                busyId: null,
                errorCode: (error && error.code) || 'NETWORK_ERROR'
            }));
    }

    handleConfirmPending () {
        const {pending, pendingValue} = this.state;
        if (!pending) return null;
        if (pending.type === 'resetPassword' && pendingValue.length < MIN_PASSWORD_LENGTH) {
            this.setState({errorCode: 'INVALID_PASSWORD'});
            return null;
        }
        this.setState({busyId: pending.user ? pending.user.id : pending.project.id, errorCode: null});
        const action = pending.type === 'deleteUser' ?
            deleteAdminUser(pending.user.id) :
            pending.type === 'deleteProject' ?
                deleteAdminProject(pending.project.id) :
                resetAdminUserPassword(pending.user.id, pendingValue);
        return action.then(() => {
            this.setState({pending: null, pendingValue: ''});
            return this.handleReload();
        }).then(() => this.setState({busyId: null, statusMessage: 'done'}))
            .catch(error => this.setState({
                busyId: null,
                errorCode: (error && error.code) || 'NETWORK_ERROR'
            }));
    }

    renderError () {
        const {errorCode} = this.state;
        if (!errorCode) return null;
        return (
            <div
                className={styles.error}
                id="admin-error"
            >
                {/* describeAuthError returns the message descriptor itself, so it
                    is formatted directly rather than spread onto a component. */}
                {this.props.intl.formatMessage(describeAuthError(errorCode))}
            </div>
        );
    }

    renderPending () {
        const {pending, pendingValue, busyId} = this.state;
        if (!pending) return null;
        const isPassword = pending.type === 'resetPassword';
        return (
            <div
                className={styles.confirm}
                id="admin-confirm"
            >
                <div className={styles.confirmText}>
                    {pending.type === 'deleteUser' ? (
                        <FormattedMessage
                            {...messages.confirmDeleteUser}
                            values={{account: pending.user.account}}
                        />
                    ) : pending.type === 'deleteProject' ? (
                        <FormattedMessage
                            {...messages.confirmDeleteProject}
                            values={{name: pending.project.name}}
                        />
                    ) : (
                        <FormattedMessage
                            {...messages.confirmResetPassword}
                            values={{account: pending.user.account}}
                        />
                    )}
                </div>
                {isPassword ? (
                    <input
                        autoFocus
                        className={styles.confirmInput}
                        id="admin-new-password"
                        type="password"
                        value={pendingValue}
                        onChange={this.handlePendingValueChange}
                    />
                ) : null}
                <div className={styles.confirmButtons}>
                    <button
                        className={styles.secondaryButton}
                        id="admin-confirm-cancel"
                        type="button"
                        onClick={this.handleCancelPending}
                    >
                        <FormattedMessage {...messages.cancel} />
                    </button>
                    <button
                        className={styles.dangerButton}
                        disabled={busyId !== null}
                        id="admin-confirm-ok"
                        type="button"
                        onClick={this.handleConfirmPending}
                    >
                        <FormattedMessage {...messages.confirm} />
                    </button>
                </div>
            </div>
        );
    }

    renderOverview () {
        const {overview} = this.state;
        if (!overview) return null;
        const stats = [
            {id: 'users', label: messages.statUsers, value: overview.users},
            {id: 'admins', label: messages.statAdmins, value: overview.admins},
            {id: 'disabled', label: messages.statDisabled, value: overview.disabled},
            {id: 'projects', label: messages.statProjects, value: overview.projects},
            {id: 'storage', label: messages.statStorage, value: formatBytes(overview.bytes)}
        ];
        return (
            <div className={styles.stats}>
                {stats.map(stat => (
                    <div
                        className={styles.statCard}
                        key={stat.id}
                    >
                        <div
                            className={styles.statValue}
                            id={`admin-stat-${stat.id}`}
                        >
                            {stat.value}
                        </div>
                        <div className={styles.statLabel}>
                            <FormattedMessage {...stat.label} />
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    renderUsers () {
        const {users, busyId} = this.state;
        if (users.length === 0) {
            return (
                <div className={styles.empty}>
                    <FormattedMessage {...messages.emptyUsers} />
                </div>
            );
        }
        return (
            <table
                className={styles.table}
                id="admin-users-table"
            >
                <thead>
                    <tr>
                        <th><FormattedMessage {...messages.columnAccount} /></th>
                        <th><FormattedMessage {...messages.columnDisplayName} /></th>
                        <th><FormattedMessage {...messages.columnCreated} /></th>
                        <th><FormattedMessage {...messages.columnProjects} /></th>
                        <th><FormattedMessage {...messages.columnRole} /></th>
                        <th><FormattedMessage {...messages.columnStatus} /></th>
                        <th><FormattedMessage {...messages.columnActions} /></th>
                    </tr>
                </thead>
                <tbody>
                    {users.map(user => (
                        <tr
                            className={user.disabled ? styles.rowDisabled : null}
                            key={user.id}
                        >
                            <td className={styles.strong}>{user.account}</td>
                            <td>{user.displayName}</td>
                            <td className={styles.muted}>{user.createdAt}</td>
                            <td>{user.projectCount === null ? '—' : user.projectCount}</td>
                            <td>
                                <FormattedMessage
                                    {...(user.role === ADMIN_ROLE ?
                                        messages.roleAdmin : messages.roleUser)}
                                />
                            </td>
                            <td>
                                <FormattedMessage
                                    {...(user.disabled ?
                                        messages.statusDisabled : messages.statusActive)}
                                />
                            </td>
                            <td className={styles.actions}>
                                <button
                                    className={styles.smallButton}
                                    disabled={busyId === user.id}
                                    id={`admin-toggle-disabled-${user.account}`}
                                    type="button"
                                    /* eslint-disable-next-line react/jsx-no-bind */
                                    onClick={() => this.handleToggleDisabled(user)}
                                >
                                    <FormattedMessage
                                        {...(user.disabled ? messages.enable : messages.disable)}
                                    />
                                </button>
                                <button
                                    className={styles.smallButton}
                                    disabled={busyId === user.id}
                                    id={`admin-toggle-role-${user.account}`}
                                    type="button"
                                    /* eslint-disable-next-line react/jsx-no-bind */
                                    onClick={() => this.handleToggleRole(user)}
                                >
                                    <FormattedMessage
                                        {...(user.role === ADMIN_ROLE ?
                                            messages.demote : messages.promote)}
                                    />
                                </button>
                                <button
                                    className={styles.smallButton}
                                    disabled={busyId === user.id}
                                    id={`admin-reset-password-${user.account}`}
                                    type="button"
                                    /* eslint-disable-next-line react/jsx-no-bind */
                                    onClick={() => this.handleAskResetPassword(user)}
                                >
                                    <FormattedMessage {...messages.resetPassword} />
                                </button>
                                <button
                                    className={styles.dangerSmallButton}
                                    disabled={busyId === user.id}
                                    id={`admin-delete-user-${user.account}`}
                                    type="button"
                                    /* eslint-disable-next-line react/jsx-no-bind */
                                    onClick={() => this.handleAskDeleteUser(user)}
                                >
                                    <FormattedMessage {...messages.remove} />
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    }

    renderProjects () {
        const {projects, busyId} = this.state;
        if (projects.length === 0) {
            return (
                <div className={styles.empty}>
                    <FormattedMessage {...messages.emptyProjects} />
                </div>
            );
        }
        return (
            <table
                className={styles.table}
                id="admin-projects-table"
            >
                <thead>
                    <tr>
                        <th><FormattedMessage {...messages.columnProjects} /></th>
                        <th><FormattedMessage {...messages.columnOwner} /></th>
                        <th><FormattedMessage {...messages.columnSize} /></th>
                        <th><FormattedMessage {...messages.columnUpdated} /></th>
                        <th><FormattedMessage {...messages.columnActions} /></th>
                    </tr>
                </thead>
                <tbody>
                    {projects.map(project => (
                        <tr key={project.id}>
                            <td className={styles.strong}>{project.name}</td>
                            <td>
                                {project.ownerAccount || (
                                    <span className={styles.muted}>
                                        <FormattedMessage {...messages.orphaned} />
                                    </span>
                                )}
                            </td>
                            <td>{formatBytes(project.size)}</td>
                            <td className={styles.muted}>{project.updatedAt}</td>
                            <td className={styles.actions}>
                                <button
                                    className={styles.dangerSmallButton}
                                    disabled={busyId === project.id}
                                    id={`admin-delete-project-${project.id}`}
                                    type="button"
                                    /* eslint-disable-next-line react/jsx-no-bind */
                                    onClick={() => this.handleAskDeleteProject(project)}
                                >
                                    <FormattedMessage {...messages.remove} />
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    }

    render () {
        const {tab, isLoading} = this.state;
        return (
            <Modal
                className={styles.modalContent}
                id="admin-panel-modal"
                onRequestClose={this.props.onRequestClose}
            >
                <Box
                    className={styles.body}
                    id="admin-panel-dialog"
                >
                    <div className={styles.header}>
                        <h2 className={styles.title}>
                            <FormattedMessage {...messages.title} />
                        </h2>
                        <button
                            className={styles.secondaryButton}
                            id="admin-reload"
                            type="button"
                            onClick={this.handleReload}
                        >
                            <FormattedMessage {...messages.reload} />
                        </button>
                    </div>

                    <div className={styles.tabs}>
                        <button
                            className={tab === TAB_OVERVIEW ? styles.tabActive : styles.tab}
                            id="admin-tab-overview"
                            type="button"
                            onClick={this.handleSelectOverview}
                        >
                            <FormattedMessage {...messages.tabOverview} />
                        </button>
                        <button
                            className={tab === TAB_USERS ? styles.tabActive : styles.tab}
                            id="admin-tab-users"
                            type="button"
                            onClick={this.handleSelectUsers}
                        >
                            <FormattedMessage {...messages.tabUsers} />
                        </button>
                        <button
                            className={tab === TAB_PROJECTS ? styles.tabActive : styles.tab}
                            id="admin-tab-projects"
                            type="button"
                            onClick={this.handleSelectProjects}
                        >
                            <FormattedMessage {...messages.tabProjects} />
                        </button>
                    </div>

                    {this.renderError()}

                    <div className={styles.content}>
                        {isLoading ? (
                            <div className={styles.empty}>
                                <FormattedMessage {...messages.loading} />
                            </div>
                        ) : (
                            <React.Fragment>
                                {tab === TAB_OVERVIEW ? this.renderOverview() : null}
                                {tab === TAB_USERS ? this.renderUsers() : null}
                                {tab === TAB_PROJECTS ? this.renderProjects() : null}
                            </React.Fragment>
                        )}
                    </div>

                    {this.renderPending()}
                </Box>
            </Modal>
        );
    }
}

AdminPanel.propTypes = {
    intl: intlShape.isRequired,
    onRequestClose: PropTypes.func.isRequired
};

export default injectIntl(AdminPanel);
